import { Tooltip, TooltipProps } from '@taxbit-private/cosmic'
import React, { forwardRef } from 'react'

import Permission from 'constants/Permissions'
import { useUser } from 'contexts/UserContext'

import getDefaultPermissionRequiredLabel from './getDefaultPermissionRequiredLabel'

export type PermissionTooltipRenderProps = {
  userHasPermission: boolean
  tooltipContent: React.ReactNode
}

type PermissionTooltipRenderFn = (props: PermissionTooltipRenderProps) => React.ReactElement

type PermissionTooltipProps = Omit<TooltipProps, 'content' | 'target' | 'children'> & {
  content?: React.ReactNode
  children: PermissionTooltipRenderFn
  permissionName: Permission
}

/**
 * The Cosmic Tippy component requires a forwardRef or a DOMNode to be passed to it as the target.
 */
export const RefWrapper = forwardRef<HTMLDivElement, { children: React.ReactElement }>(
  ({ children }, ref) => {
    return <div ref={ref}>{children}</div>
  }
)
RefWrapper.displayName = 'RefWrapper'

/**
 * A tooltip with built-in permission checking. Accepts a child function that will
 * be called with the result of the permission check, stored in the `userHasPermission` prop.
 */
const PermissionTooltip: React.FC<PermissionTooltipProps> = ({
  children,
  permissionName,
  ...tooltipProps
}) => {
  const { hasPermission } = useUser()

  const { content, ...restTooltipProps } = tooltipProps

  const tooltipContent = content || getDefaultPermissionRequiredLabel(permissionName)

  const userHasPermission = hasPermission(permissionName)

  return userHasPermission ? (
    children({ userHasPermission, tooltipContent })
  ) : (
    <Tooltip
      {...restTooltipProps}
      content={tooltipContent}
      target={<RefWrapper>{children({ userHasPermission, tooltipContent })}</RefWrapper>}
    />
  )
}

export default PermissionTooltip
